import { Badge, Code, Group, Paper, Stack, Text } from '@mantine/core';
import { useState } from 'react';
import { Select } from 'src/design-system/Select';
import { MetadataRow } from './MetadataRow';
import { CODE_BLOCK_STYLE } from './styles';
import { LlmStepTrace } from './types';

const ALL_STEPS = 'all';

function TraceCard({ trace, index }: { trace: LlmStepTrace; index: number }) {
  return (
    <Paper withBorder p="sm" radius="md">
      <Stack gap="xs">
        <Group justify="space-between">
          <Group gap="xs">
            <Text fw={600} size="sm">
              {index + 1}. {trace.step}
            </Text>
            {trace.error ? (
              <Badge color="red" size="sm">
                {trace.error}
              </Badge>
            ) : null}
          </Group>
          <Text size="xs" c="dimmed">
            {trace.latencyMs != null ? `${(trace.latencyMs / 1000).toFixed(1)}s` : ''}
          </Text>
        </Group>
        <MetadataRow label="Model" value={trace.model} />
        <MetadataRow label="Finish reason" value={trace.finishReason} />
        {trace.reasoning ? (
          <>
            <Text size="sm" fw={600}>
              Reasoning
            </Text>
            <Code block style={CODE_BLOCK_STYLE}>
              {trace.reasoning}
            </Code>
          </>
        ) : (
          <Text size="sm" c="dimmed">
            No reasoning captured (non-thinking model or reasoning not returned)
          </Text>
        )}
        {trace.content ? (
          <>
            <Text size="sm" fw={600}>
              Output
            </Text>
            <Code block style={CODE_BLOCK_STYLE}>
              {trace.content}
            </Code>
          </>
        ) : null}
      </Stack>
    </Paper>
  );
}

type LlmStepTracesPanelProps = {
  traces: LlmStepTrace[];
};

export function LlmStepTracesPanel({ traces }: LlmStepTracesPanelProps) {
  const [selectedStep, setSelectedStep] = useState<string>(ALL_STEPS);

  if (traces.length === 0) {
    return (
      <Text size="sm" c="dimmed">
        No LLM step traces recorded for this file. Traces are captured per detection run; re-run
        detection to record them.
      </Text>
    );
  }

  const steps = [...new Set(traces.map(trace => trace.step))];
  const visible =
    selectedStep === ALL_STEPS ? traces : traces.filter(trace => trace.step === selectedStep);

  return (
    <Stack gap="sm">
      <Select
        label="Step"
        data={[
          { value: ALL_STEPS, label: `All steps (${traces.length} calls)` },
          ...steps.map(step => ({ value: step, label: step })),
        ]}
        value={selectedStep}
        onChange={value => setSelectedStep(value ?? ALL_STEPS)}
        searchable
      />
      {visible.map((trace, index) => (
        <TraceCard key={`${trace.step}-${index}`} trace={trace} index={index} />
      ))}
    </Stack>
  );
}
